import React from 'react';
import { Modal, Typography, Table, Tag, Divider } from 'antd';
import { constants } from '../../constants/common';

const { Title, Text } = Typography;

interface Target {
    period: string;
    targetFor: string;
    targetValue: number;
    type: string;
}

interface TargetPopupProps {
    visible: boolean;
    targets: Target[];
    onClose: () => void;
}

const TargetPopup: React.FC<TargetPopupProps> = ({ visible, targets, onClose }) => {
    // Get target display name from value
    const getTargetForName = (value: string) => {
        if (value.startsWith('Recruitment -')) {
            return value; // Already formatted
        }
        // Check HR targets
        const hrTarget = constants.TARGET_FOR.find(t => t.value === value);
        if (hrTarget) {
            return hrTarget.name;
        }
        // Check BDE targets
        const bdeTarget = constants.BDE_TARGET_TYPES.find(t => t.value === value);
        if (bdeTarget) {
            return bdeTarget.name;
        }
        // Check quarterly targets (Revenue, Deals, Accounts, Leads)
        const quarterlyTargetMap: { [key: string]: string } = {
            'Revenue': 'Revenue',
            'Deals': 'Deals',
            'Accounts': 'Accounts',
            'Leads': 'Leads'
        };
        if (quarterlyTargetMap[value]) {
            return quarterlyTargetMap[value];
        }
        // Fallback to original value
        return value;
    };

    const columns = [
        {
            title: 'Period',
            dataIndex: 'period',
            key: 'period',
            render: (period: string) => {
                const colorMap: { [key: string]: string } = {
                    'Daily': 'blue',
                    'Weekly': 'green',
                    'Monthly': 'orange',
                    'Quarterly': 'purple'
                };
                return <Tag color={colorMap[period] || 'default'}>{period}</Tag>;
            }
        },
        {
            title: 'Target For',
            dataIndex: 'targetFor',
            key: 'targetFor',
            render: (targetFor: string) => getTargetForName(targetFor)
        },
        {
            title: 'Target Value',
            dataIndex: 'targetValue',
            key: 'targetValue',
            render: (value: number) => <Text strong>{value}</Text>
        }
    ];

    return (
        <Modal
            title={
                <div>
                    <Title level={4} style={{ margin: 0 }}>You have this target</Title>
                    <Text type="secondary">Your targets for today</Text>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={600}
            centered
            className="target-popup-modal"
        >
            <div className="target-popup-content">
                {targets.length > 0 ? (
                    <>
                        <Table
                            columns={columns}
                            dataSource={targets.map((target, index) => ({
                                key: index,
                                ...target
                            }))}
                            pagination={false}
                            size="small"
                        />
                        <Divider />
                        <div className="target-popup-footer">
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Good luck achieving your targets today! 🎯
                            </Text>
                        </div>
                    </>
                ) : (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Text type="secondary">No targets set for you at this time.</Text>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default TargetPopup;

